import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { User } from 'cde-fe-organization-registration-dialog';
import { of } from 'rxjs';
import { map, tap, exhaustMap, finalize, catchError, take } from 'rxjs/operators';
import * as RootState from '..';
import * as AuthActions from './auth.actions';
import { SetOrganisationChanging, RehydrateState } from '../organisations/organisations.actions';
import { AuthServiceWrapper } from './../../services/auth.service';

@Injectable()
export class AuthEffects {
  readonly actions$ = inject(Actions);
  readonly auth = inject(AuthServiceWrapper);
  readonly router = inject(Router);
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LOGIN),
        tap(() => {
          this.auth.login();
        })
      ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGIN_REDIRECT),
      map(() => new AuthActions.LoginRedirectSuccess())
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LOGIN_SUCCESS),
        map((action: AuthActions.LoginSuccess) => action.payload),
        tap(() => {
          // Set is social user after login from id token...
          this.store.dispatch(new AuthActions.SetIsSocialUser(this.auth.getIsSocialFromIdToken()));

          // Rehydrate when login is complete so that the token is valid for the
          // rehydrate API calls. The true flag is needed to indicate at the end
          // of the rehydate when the feature toggles are present that we might
          // need to handle the redirectUrl in the localstorage
          this.store.dispatch(new RehydrateState(true));
        })
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LOGIN_FAILURE),
        tap(() => {
          void this.router.navigate(['']);
        })
      ),
    { dispatch: false }
  );

  checkLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.CHECK_LOGIN),
      exhaustMap(() =>
        this.auth.checkLogin().pipe(
          take(1),
          map((res: User) => new AuthActions.CheckLoginSuccess(res)),
          catchError(error => of(new AuthActions.CheckLoginFailure(error))),
          finalize(() => this.store.dispatch(new AuthActions.CheckLoginComplete()))
        )
      )
    )
  );

  checkLoginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.CHECK_LOGIN_SUCCESS),
        tap(() => {
          // Rehydrate when login is complete so that the token is valid for the
          // rehydrate API calls
          this.store.dispatch(new RehydrateState());
        })
      ),
    { dispatch: false }
  );

  checkFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.CHECK_LOGIN_FAILURE),
        tap(error => {
          if (error.payload !== 'unauthenticated') {
            this.auth.relogin();
          }
        })
      ),
    { dispatch: false }
  );

  /*
  @Effect()
  loginCallback$ = this.actions$.pipe(
    ofType(AuthActions.LOGIN_CALLBACK),
    switchMap(() => {
      return this.auth.handleCallback();

    }),
    switchMap((res) => [
      new AuthActions.GetUser()
    ]),
    switchMap((res) => [
      new AuthActions.LoginSuccess({
        user: res[0],
        authenticated: res[1]
      })
    ]),
    finalize(() => this.store.dispatch(new AuthActions.LoginComplete()))
  ); */

  loginCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGIN_CALLBACK),
      exhaustMap(() =>
        this.auth.handleCallback().pipe(
          take(1),
          map(
            res =>
              new AuthActions.LoginSuccess({
                user: res[0],
                authenticated: res[1],
              })
          )
        )
      ),
      catchError(error => of(new AuthActions.LoginFailure(error))),
      finalize(() => this.store.dispatch(new AuthActions.LoginComplete()))
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LOGOUT),
        tap(() => {
          localStorage.clear();
          this.auth.logout();
        })
      ),
    { dispatch: false }
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.GET_USER),
      exhaustMap((action: AuthActions.GetUser) =>
        this.auth.getUserNoCache$(action.auth0Id).pipe(
          map(user => new AuthActions.GetUserSuccess(user, action.setNewCurrentOrg)),
          catchError(error => of(new AuthActions.GetUserFailure(error))),
          finalize(() => {
            this.store.dispatch(new SetOrganisationChanging(false));
            return this.store.dispatch(new AuthActions.GetUserComplete());
          })
        )
      )
    )
  );
}
